import { request, requestWithMsg, MsgResult } from './request';
import { RelatedGroup } from './cascade';

// 列表页批量操作 (2026-09-10, 对应后端 internal/app/batch).
//
// 一个通用端点服务所有列表: 目标编码与单条请求体都由页面按它调单条接口时的同一套规则组好,
// 后端只做校验/门禁/建工单, 真正的写入是审批回放时各资源原有的 ApplyChange 干的 ——
// 所以接新列表不需要动后端, 只要传对 resource 与 buildItem。
//
// 审批粒度: N 个目标进**一张**工单, 审批人整批准或整批驳, 回放在同一事务里逐项执行, 全成全败。

// BATCH_TIMEOUT_MS 批量操作的超时: request 默认 30s 是按"页面读接口"定的, 批量删除/发布
// 一张单可能带上千个目标, 逐个跑级联预检与落库会久, 给 3 分钟别被误杀.
const BATCH_TIMEOUT_MS = 180_000;

/** 资源域 (后端 changereq.Resource* 常量, 也是审批工单的 targetType). */
export type BatchResource =
    | 'material'
    | 'device'
    | 'category'
    | 'dict'
    | 'dict_category'
    | 'cap_type'
    | 'gallery'
    | 'tool'
    | 'tip'
    | 'phys_schema'
    | 'process_arch'
    | 'param_schema'
    | 'mm_group'
    | 'mm_mode'
    | 'mm_device_ref'
    | 'device_schema'
    | 'sku_schema'
    | 'device_ext_value'
    | 'schema_field'
    | 'device_material_compat'
    | 'param_row'
    // apitoken 不在后端 changereq 资源表里 (删除即时生效, 没有审批回放器):
    // 只能配合 BatchActions 的 localDelete 用, 传给 /v1/batch/* 会被拒.
    | 'apitoken';

/** 批量目标: targetCode + 该资源单条接口的请求体 (回放时原样交给它的 ApplyChange). */
export interface BatchItem {
    targetCode: string;
    data?: unknown;
}

/** 有阻塞项的目标: 这条删不了, 弹窗要把它列出来并说明原因. */
export interface BlockedTarget {
    targetCode: string;
    reasons: string[];
}

export interface BatchDeletePreview {
    /** 合并后的关联数据分组 (同 key 累加, 必删/阻塞取或) */
    groups: RelatedGroup[];
    blocked: BlockedTarget[];
}

export type BatchResult = MsgResult<{ taskCode?: string; count?: number } | null>;

/** 后端 batch.maxPreviewTargets: 一次批量删除的目标数上限 (预检是逐目标的 count 查询). */
export const BATCH_DELETE_MAX = 50;

/**
 * 批量删除前的合并级联预检: 逐个目标跑各资源的 delete-preview 并按组 key 合并,
 * 用户勾一次就对整批生效。无级联的资源返回空分组 (页面直接进二次确认)。
 */
export function fetchBatchDeletePreview(
    resource: BatchResource,
    targetCodes: string[],
): Promise<BatchDeletePreview> {
    return request<BatchDeletePreview>('/v1/batch/delete-preview', {
        method: 'POST',
        body: { resource, targetCodes },
        timeoutMs: BATCH_TIMEOUT_MS,
    });
}

/**
 * 批量删除动作: 删除类动作在各资源里不同名 —— SKU 是 delete_sku, 材料详情里的 SKU 解绑是
 * unbind_sku, 其余都是 delete (缺省)。后端按白名单校验。
 */
export type BatchDeleteAction = 'delete' | 'delete_sku' | 'unbind_sku';

/** 批量删除 (走变更审批工单, 一张单含全部目标). */
export function batchDelete(
    resource: BatchResource,
    items: BatchItem[],
    action?: BatchDeleteAction,
): Promise<BatchResult> {
    return requestWithMsg('/v1/batch/delete', {
        method: 'POST',
        body: { resource, items, action },
        timeoutMs: BATCH_TIMEOUT_MS,
    });
}

/** 批量发布 / 取消发布 (仅 material / device: 只有它们有发布状态). */
export function batchPublish(
    resource: 'material' | 'device',
    targetCodes: string[],
    publish: boolean,
): Promise<BatchResult> {
    return requestWithMsg('/v1/batch/publish', {
        method: 'POST',
        body: { resource, publish, items: targetCodes.map((targetCode) => ({ targetCode })) },
        timeoutMs: BATCH_TIMEOUT_MS,
    });
}

/**
 * 批量部分更新 (走变更审批工单, 一张单含全部目标). 后端按白名单开放资源,
 * 目前只有 mm_mode (加工模式字典「迁移」: data = {value, groupCode}).
 */
export function batchUpdate(
    resource: 'mm_mode',
    items: BatchItem[],
): Promise<BatchResult> {
    return requestWithMsg('/v1/batch/update', {
        method: 'POST',
        body: { resource, items },
        timeoutMs: BATCH_TIMEOUT_MS,
    });
}
